import { Input, Field } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { AttributeWrapper } from "./AttributeWrapper";
import { CraAttributeNumber, CodingAttributes } from "@keita/lanepatrol-sdk";
import { parseAttributeInputValue } from "../../../../../../coding/utils/attribute-value";
import { useStores } from "../../../../../../shared/stores/RootStore";
import { useAttributeHighlight } from "../../hooks/useAttributeHighlight";
import { CraAttributeUtils } from "../../../../../../coding/utils/attribute-utils";
import { CraNodeCoding } from "../../../../../../coding/models/CraNodeCoding";

const NumberAttributeBase = ({
  attributeDefinition,
  attributeDisabled,
  readOnly,
  nodeCoding,
  compact,
}: {
  attributeDefinition: CraAttributeNumber;
  attributeDisabled: boolean;
  readOnly: boolean;
  nodeCoding: CraNodeCoding;
  compact: boolean;
}) => {
  const { codingStore } = useStores();
  const highlighted = useAttributeHighlight(attributeDefinition);

  if (attributeDisabled) return null;

  const isSuggested = nodeCoding.suggestions.includes(attributeDefinition.name);

  const currentValue = nodeCoding.attributes[
    attributeDefinition.name as keyof CodingAttributes
  ] as null | number;

  const inputValue = currentValue === null ? "" : currentValue?.toString();

  return (
    <AttributeWrapper
      attributeDefinition={attributeDefinition}
      isSuggested={isSuggested}
      label={CraAttributeUtils.getLabel(attributeDefinition.name)}
      compact={compact}
    >
      <Field.Root invalid={isInvalid(currentValue, attributeDefinition)}>
        <Input
          disabled={readOnly}
          cursor={readOnly ? "not-allowed" : undefined}
          opacity={readOnly ? 0.5 : 1}
          size={compact ? "xs" : "md"}
          fontSize={compact ? "xs" : "md"}
          transitionDuration="0.2s"
          bgColor={highlighted ? `primary.100` : "white"}
          type="number"
          value={inputValue}
          min={attributeDefinition.min}
          max={attributeDefinition.max}
          onChange={(e) => {
            if (readOnly) return;

            const parsedValue = parseAttributeInputValue(
              attributeDefinition,
              e.target.value,
            ) as number | null;

            codingStore.codeAttribute(attributeDefinition.name, parsedValue);
          }}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </Field.Root>
    </AttributeWrapper>
  );
};

export const NumberAttribute = observer(NumberAttributeBase);

const isInvalid = (
  value: number | null,
  attributeDefinition: CraAttributeNumber,
) => {
  if (value === null) return false;

  if (attributeDefinition.min !== undefined && value < attributeDefinition.min)
    return true;

  if (attributeDefinition.max !== undefined && value > attributeDefinition.max)
    return true;

  return false;
};
